import { axios } from '@ben/api/axios';
import { PayrollPeriodEnum } from '@ben/api/gen/codegen.schemas';
import { benLanguageCodeSchema } from '@ben/common/languages';
import { CardProvider } from '@ben/shared/types/card-provider';
import { optionalString } from '@ben/shared/utils/zod';
import * as Sentry from '@sentry/react';
import { z } from 'zod';

export const employerMeSchema = z.object({
  id: z.number(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  phoneNumber: z.string().nullable(),
  mobileNumber: z.string().nullable(),
  mobileCountryCode: z.string().nullable(),
  isHr: z.boolean(),
  isSocialAuth: z.boolean(),
  employer: z.object({
    name: z.string(),
    iconUrl: optionalString,
    currency: z.string(),
    pk: z.number(),
    cardProvider: z.nativeEnum(CardProvider).nullable(),
    hasHrisActive: z.boolean(),
    managedAccount: z
      .object({
        currency: z.string(),
        id: z.number(),
      })
      .nullable(),
  }),
  weavrPasswordSet: z.boolean(),
  mobilePhoneVerified: z.boolean(),
  emailVerified: z.boolean(),
  hasCardProduct: z.boolean(),
  hasBenefits: z.boolean(),
  hasAllowances: z.boolean(),
  isRootUser: z.boolean(),
  isKybComplete: z.boolean(),
  isDelegatedRoot: z.boolean(),
  hasCorporate: z.boolean(),
  hasTopupCards: z.boolean(),
  isManager: z.boolean(),
  pendingActions: z
    .object({
      approvalsCount: z.number().nullable(),
      invoicesCount: z.number().nullable(),
      enrollmentsCount: z.number().nullable(),
    })
    .nullable(),
  managedAccountsCreated: z.boolean(),
  hasSelfManagedBenefits: z.boolean(),
  permissions: z.array(z.string()).nullable(),
  preferredLanguage: benLanguageCodeSchema.nullable(),
  requireEmployeeSalary: z.boolean(),
  showEmployeeCompensation: z.boolean(),
  hasHrisIntegrations: z.boolean(),
  numberOfEmployeesMissingRequirements: z.number().nullable(),
  intercomIdentityHash: z.string().nullable(),
  payrollPeriod: z.nativeEnum(PayrollPeriodEnum).optional(),
  disableInviteEmail: z.boolean(),
});

export type EmployerMe = z.infer<typeof employerMeSchema>;

export const getEmployerMe = async (): Promise<EmployerMe> => {
  const response = await axios.get(`ecc/v1.0/me/`);
  try {
    return employerMeSchema.parse(response.data);
  } catch (error) {
    Sentry.captureException(error);
    return response.data;
  }
};

export const updateEmployerUserLanguage: (
  language: string
) => Promise<EmployerMe> = async (language: string): Promise<EmployerMe> => {
  const response = await axios.patch('ecc/v1.0/me/', {
    preferredLanguage: language,
  });
  return response.data as EmployerMe;
};
